import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, Search, X } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import { UserContext } from 'contexts/UserContext';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Progress } from 'shadcn/components/ui/Progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'shadcn/components/ui/Table';
import { Notifications } from 'shared';
import { useFbmIngestResultsQuery } from 'shared/hooks/fbm/useFbmIngestResultsQuery';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';
import dayjs from 'utils/dayjsConfig';
import { snakeCaseToTitleCase } from 'utils/fbm/stringUtils';

import { IngestResultTypeFilter } from './IngestResultTypeFilter';

const DETAIL_LABELS = {
    orderTypeId: 'Order type',
    orderTypeName: null, // shown under Order type, skip as separate row
    day: 'Day',
    zip: 'ZIP code',
    reason: 'Reason',
    itemSKU: 'Item SKU',
    city: 'City',
};

function getDetailLabel(key) {
    const label = DETAIL_LABELS[key];
    if (label === null) return null;
    if (label != null) return label;
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}

function hasDetails(details) {
    if (details == null) return false;
    if (Array.isArray(details)) return details.length > 0;
    if (typeof details === 'object') {
        return Object.keys(details).some((key) => getDetailLabel(key) !== null);
    }
    return true;
}

const formatOrderTypeName = snakeCaseToTitleCase;

function formatDetailsSummary(details) {
    if (!hasDetails(details)) return '—';
    return 'View details';
}

function formatDetailsForModal(details) {
    if (details == null || (Array.isArray(details) && details.length === 0)) {
        return <p className="text-muted-foreground text-sm">No details.</p>;
    }
    if (Array.isArray(details)) {
        return (
            <pre className="text-sm overflow-auto max-h-[60vh] rounded border p-3 bg-muted/30">
                {JSON.stringify(details, null, 2)}
            </pre>
        );
    }
    if (typeof details === 'object') {
        const entries = Object.entries(details).filter(([key]) => getDetailLabel(key) !== null);
        if (entries.length === 0) {
            return <p className="text-muted-foreground text-sm">No details.</p>;
        }
        return (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm overflow-auto max-h-[60vh]">
                {entries.map(([key, value]) => {
                    const label = getDetailLabel(key);
                    const displayLabel = label ?? key;
                    let displayValue = value;
                    if (key === 'orderTypeId' && details.orderTypeName != null) {
                        displayValue = formatOrderTypeName(details.orderTypeName);
                    } else if (typeof value === 'object' && value !== null) {
                        displayValue = JSON.stringify(value);
                    } else {
                        displayValue = String(value);
                    }
                    return (
                        <React.Fragment key={key}>
                            <dt className="font-medium text-muted-foreground py-0.5">{displayLabel}</dt>
                            <dd className="break-words py-0.5 text-foreground">{displayValue}</dd>
                        </React.Fragment>
                    );
                })}
            </dl>
        );
    }
    return <p className="text-sm">{String(details)}</p>;
}

function IngestResultActionsCell({ fbmOrderNum, onGoToOrderDeepDive }) {
    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className="whitespace-nowrap"
            disabled={!fbmOrderNum}
            onClick={(e) => onGoToOrderDeepDive(e, fbmOrderNum)}
        >
            Go To Deep Dive
        </Button>
    );
}

const renderIngestResultActionsCell = ({ row, table }) => {
    const onGoToOrderDeepDive = table.options.meta?.onGoToOrderDeepDive;
    return <IngestResultActionsCell fbmOrderNum={row.original.fbmOrderNum} onGoToOrderDeepDive={onGoToOrderDeepDive} />;
};

/**
 * @param {function(string|undefined): void} [onQChange] - Updates URL when debounced search changes (see FBMViewOrders)
 * @param {string} [q] - Ingest list search string from URL (`q` query param), for shareable links
 * @param {string[]} [typesParam] - Selected ingest types from URL (`types` query param)
 * @param {function(string[]): void} [onTypesChange] - Updates URL when type filters change
 */
function ViewAllFBMIngestResults({ onQChange, onTypesChange, q: qParam, typesParam = [] } = {}) {
    // State
    const { fbmAccount } = useContext(UserContext);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });
    const [selectedTypes, setSelectedTypes] = useState(typesParam);
    const [searchInput, setSearchInput] = useState(qParam ?? '');
    const debouncedSearch = useDebouncedValue(searchInput, 300);
    const q = debouncedSearch.trim() || undefined;
    const types = selectedTypes.length > 0 ? selectedTypes : undefined;
    const [selectedRow, setSelectedRow] = useState(null);
    const skipQPageResetOnMount = useRef(true);
    const history = useHistory();

    const handleGoToOrderDeepDive = useCallback(
        (event, fbmOrderNum) => {
            event.stopPropagation();
            if (!fbmOrderNum) return;
            history.push(`/fbm/orders?tab=1&fbmOrderNum=${encodeURIComponent(fbmOrderNum)}`);
        },
        [history],
    );

    const skipTypesPageResetOnMount = useRef(true);
    const columns = useMemo(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
            },
            {
                header: `Created Time (${dayjs().tz(dayjs.tz.guess()).format('z')})`,
                accessorFn: (row) => dayjs(row.createdAt).format('MM/DD/YYYY h:mm a'),
            },
            {
                header: 'FBM Order Number',
                accessorKey: 'fbmOrderNum',
            },
            {
                header: 'Type',
                accessorKey: 'type',
            },
            {
                header: 'Details',
                accessorKey: 'details',
                cell: ({ row }) => formatDetailsSummary(row.original.details),
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: renderIngestResultActionsCell,
            },
        ],
        [],
    );

    const {
        data: queryData,
        error,
        isError,
        isFetching,
    } = useFbmIngestResultsQuery({
        fbmAccountName: fbmAccount.fbmAccountName,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        q,
        types,
    });

    const data = queryData?.ingestResults ?? [];
    const pageMeta = queryData?.page;

    // Handlers
    const handleOnPaginationChange = (updater) => {
        setPagination((prev) => (typeof updater === 'function' ? updater(prev) : updater));
    };

    const handleSelectedTypesChange = (nextTypes) => {
        setSelectedTypes(nextTypes);
    };

    useEffect(() => {
        setSearchInput(qParam ?? '');
    }, [qParam]);

    useEffect(() => {
        setSelectedTypes(typesParam);
    }, [typesParam]);

    useEffect(() => {
        onQChange?.(debouncedSearch.trim() || undefined);
    }, [debouncedSearch, onQChange]);

    useEffect(() => {
        if (skipQPageResetOnMount.current) {
            skipQPageResetOnMount.current = false;
            return;
        }
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, [q]);

    useEffect(() => {
        onTypesChange?.(selectedTypes);
    }, [selectedTypes, onTypesChange]);

    useEffect(() => {
        if (skipTypesPageResetOnMount.current) {
            skipTypesPageResetOnMount.current = false;
            return;
        }
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, [types]);

    // Table
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        meta: {
            onGoToOrderDeepDive: handleGoToOrderDeepDive,
        },
        pageCount: -1, // Total page count unknown; use API nextPage for "next" affordance
        state: {
            pagination,
        },
        onPaginationChange: handleOnPaginationChange,
    });

    const notification = isError
        ? { text: error?.message ?? 'Failed to load ingest results', level: 'error' }
        : { text: null, level: null };

    const canGoNext = !isError && pageMeta?.nextPage != null;

    return (
        <>
            <Notifications options={notification} />
            <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="w-full max-w-sm space-y-1.5">
                    <Label htmlFor="fbm-ingest-results-search" className="text-sm">
                        Search by FBM order number
                    </Label>
                    <div className="relative">
                        <Search
                            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                            aria-hidden
                        />
                        <Input
                            id="fbm-ingest-results-search"
                            type="text"
                            inputMode="search"
                            enterKeyHint="search"
                            role="searchbox"
                            autoComplete="off"
                            placeholder="Order number…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-9 pl-9 pr-9"
                        />
                        {searchInput ? (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0.5 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                onClick={() => setSearchInput('')}
                                aria-label="Clear search"
                            >
                                <X className="size-4" />
                            </Button>
                        ) : null}
                    </div>
                </div>
                <IngestResultTypeFilter
                    className="sm:shrink-0"
                    selectedTypes={selectedTypes}
                    onSelectedTypesChange={handleSelectedTypesChange}
                />
            </div>
            <div className="h-1">{isFetching && <Progress value={null} className="h-full" />}</div>
            <div className="rounded-md border">
                <Table className="w-full table-fixed">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="w-1/6 whitespace-normal break-words text-left"
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => setSelectedRow(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            className="w-1/6 whitespace-normal break-words text-left"
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    <TableCaption>
                        <div className="flex items-center justify-between gap-4 px-2">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Rows per page:</span>
                                <Select
                                    value={`${table.getState().pagination.pageSize}`}
                                    onValueChange={(value) => {
                                        table.setPageSize(Number(value));
                                    }}
                                    disabled={isFetching}
                                >
                                    <SelectTrigger className="h-8 w-[70px]">
                                        <SelectValue placeholder={table.getState().pagination.pageSize} />
                                    </SelectTrigger>
                                    <SelectContent side="top">
                                        {[10, 25, 50, 100].map((pageSize) => (
                                            <SelectItem key={pageSize} value={`${pageSize}`}>
                                                {pageSize}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => table.setPageIndex(0)}
                                    disabled={!table.getCanPreviousPage() || isFetching}
                                >
                                    <ChevronsLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage() || isFetching}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <span className="text-sm text-muted-foreground">
                                    Page {table.getState().pagination.pageIndex + 1}
                                </span>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => table.nextPage()}
                                    disabled={!canGoNext || isFetching}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                                {/* TODO: implement a go to last page, this comes with the page info endpoint / meta data */}
                            </div>
                        </div>
                    </TableCaption>
                </Table>
            </div>

            <Dialog open={selectedRow !== null} onOpenChange={(open) => !open && setSelectedRow(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Ingest details</DialogTitle>
                        {selectedRow != null && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
                                <span className="text-sm text-muted-foreground">
                                    ID: <span className="font-medium text-foreground">{selectedRow.id}</span>
                                </span>
                                {selectedRow.fbmOrderNum != null && (
                                    <span className="text-sm text-muted-foreground">
                                        FBM Order:{' '}
                                        <span className="font-medium text-foreground">{selectedRow.fbmOrderNum}</span>
                                    </span>
                                )}
                                <Badge variant="outline" className="text-xs">
                                    {selectedRow.type}
                                </Badge>
                            </div>
                        )}
                    </DialogHeader>
                    {selectedRow != null && (
                        <div className="mt-4 rounded-lg border bg-muted/30 p-4">
                            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Details
                            </p>
                            {formatDetailsForModal(selectedRow.details)}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

export { ViewAllFBMIngestResults };
